import Suggestion from '@tiptap/suggestion';
import { Editor, Extension } from '@tiptap/react';
import { Node as PMNode, Slice } from '@tiptap/pm/model';

import type { EditorDocumentInfo } from '@/hooks/llm/editor-suggestion-config';

declare module '@tiptap/react' {
  interface Storage {
    documentInfo: EditorDocumentInfo;
  }
  interface Commands<ReturnType> {
    documentInfo: {
      revertToOriginalContent: (beforeDoc: PMNode, safePosition: { from: number; to: number }) => ReturnType;
    };
  }
}

export const Commands = Extension.create({
  name: 'documentInfo',

  addStorage() {
    return {
      documentId: undefined as string | undefined,
      isComponentMounted: false,
    };
  },

  onCreate: function () {
    // TODO: Explore extracting a separate extension to handle document-specific state (e.g., documentId) via storage
    this.storage.documentId = this.options.documentId;
  },

  addOptions() {
    return {
      documentId: undefined,
      suggestion: {
        char: '/',
        allowSpaces: false,
        startOfLine: false,
        command: ({ editor }: { editor: Editor }) => onCommand('/', editor),
      },
    };
  },

  addCommands() {
    return {
      revertToOriginalContent: (beforeDoc, safePosition) => {
        return ({ tr }) => {
          const docSizeBeforeStreaming = beforeDoc?.content.size!;
          const isWholeDocumentRestore = safePosition.from === 0 && safePosition.to >= docSizeBeforeStreaming;

          if (isWholeDocumentRestore) {
            tr.replace(0, tr.doc.content.size, new Slice(beforeDoc.content, 0, 0));
            tr.setMeta('addToHistory', false);
            return true;
          }

          const originalSlice = beforeDoc!.slice(safePosition.from, safePosition.to);

          const currentDocSize = tr.doc.content.size;
          const docSizeChange = currentDocSize - docSizeBeforeStreaming;

          let restoreFrom = safePosition.from;
          let restoreTo = safePosition.to + docSizeChange;

          restoreTo = Math.max(restoreFrom + 1, Math.min(restoreTo, tr.doc.content.size));

          tr.replaceRange(restoreFrom, restoreTo, originalSlice);
          tr.setMeta('addToHistory', false);
          return true;
        };
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
        allow: (props) => {
          const { isActive, editor, state, range } = props;

          const $from = state.doc.resolve(range.from);
          const isParagraph = $from.parent.type.name === 'paragraph';

          const afterContent = $from.parent.textContent?.substring($from.parent.textContent?.indexOf('/'));
          const isValidAfterContent = !afterContent?.endsWith('  ');

          return (editor.isFocused || isActive) && isParagraph && isValidAfterContent;
        },
      }),
    ];
  },
});

export const onCommand = (char: string, editor: Editor) => {
  const { view, state } = editor;
  const { $head, $from } = view.state.selection;

  const end = $from.pos;
  const from = $head?.nodeBefore
    ? end - ($head.nodeBefore.text?.substring($head.nodeBefore.text?.lastIndexOf(char)).length ?? 0)
    : $from.start();

  const tr = state.tr.deleteRange(from, end);
  view.dispatch(tr);

  view.focus();
};
